import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import type { PhysicalFitnessAssessment } from "~/api/physicalFitnessAssessments";
import { CARDIORESPIRATORY_ENDURANCE_OPTION } from "~/api/physicalFitnessAssessments";
import { Run } from "~/icons";
import { calculateTime } from "~/utils";
import { PerformanceDetails } from ".";

interface CardiorespiratorySectionProps {
  lastPFA: PhysicalFitnessAssessment;
}

export const CardiorespiratorySection = ({
  lastPFA,
}: CardiorespiratorySectionProps) => {
  const { t } = useTranslation();

  const option = lastPFA.cardiorespiratory_endurance_option;

  const qualified = lastPFA.cardiorespiratory_endurance_score != 0;

  const totalScore = lastPFA.cardiorespiratory_endurance_score / 10;

  return (
    <PerformanceDetails.Section>
      <PerformanceDetails.Subtitle className="pb-4">
        {t("pfas.cardiorespiratory")}
      </PerformanceDetails.Subtitle>
      <PerformanceDetails.Card>
        <PerformanceDetails.CardHeader>
          <PerformanceDetails.CardTitle>
            <Run
              className={twMerge(
                qualified
                  ? "text-primary-50 dark:text-primary-80"
                  : "text-warning-95",
              )}
            />
            <PerformanceDetails.Subtitle
              className={twMerge(
                qualified
                  ? "text-primary-50 dark:text-primary-80"
                  : "text-warning-95",
              )}
            >
              {t(`pfas.${lastPFA.cardiorespiratory_endurance_option}`)}
            </PerformanceDetails.Subtitle>
          </PerformanceDetails.CardTitle>
          <PerformanceDetails.QualifiedTag
            className={twMerge(
              qualified ? "bg-info-50 dark:bg-info-30" : "bg-warning-80",
            )}
          >
            {qualified ? t("pfas.qualified") : t("pfas.not_qualified")}
          </PerformanceDetails.QualifiedTag>
        </PerformanceDetails.CardHeader>
        <PerformanceDetails.CardBody>
          <PerformanceDetails.CardResult>
            <PerformanceDetails.CardResultValue className="">
              {option === CARDIORESPIRATORY_ENDURANCE_OPTION.ONE_MIN
                ? t("pfas.time")
                : t("pfas.Shuttles")}
            </PerformanceDetails.CardResultValue>
            <PerformanceDetails.CardResultValue className="pt-2">
              {option === CARDIORESPIRATORY_ENDURANCE_OPTION.ONE_MIN
                ? calculateTime(lastPFA.cardiorespiratory_endurance_value)
                : t("pfas.shuttles", {
                    count: lastPFA.cardiorespiratory_endurance_value,
                  })}
            </PerformanceDetails.CardResultValue>
          </PerformanceDetails.CardResult>
          <PerformanceDetails.CardResult>
            <PerformanceDetails.CardResultValue className="">
              {t("pfas.score")}
            </PerformanceDetails.CardResultValue>
            <PerformanceDetails.CardResultValue className="pt-2">
              {t("pfas.over_60_points", { totalScore: totalScore })}
            </PerformanceDetails.CardResultValue>
          </PerformanceDetails.CardResult>
        </PerformanceDetails.CardBody>
      </PerformanceDetails.Card>
    </PerformanceDetails.Section>
  );
};
